import { Auswahlfeld } from './Auswahlfeld'
import { t } from './sprache'
import type { Anzeigewaehrungen, Umrechnung } from './typen'

/**
 * **Die Anzeigewährung über dem Bericht** (2.12 Abschnitt 3.21, R993, R994).
 *
 * Der Befund vom 2026-10-03: *„Wir haben noch kein Auswahlfeld für die
 * Anzeigewährung in den Berichten."* Das Feld steht neben den
 * Anzeigeoptionen; vorbelegt ist die Ausgangswährung — beim Konzern die
 * Konzernwährung, bei einer Gesellschaft ihre Landeswährung. Die Wahl gilt
 * für alle Berichte, bis sie geändert wird.
 *
 * **Es steht auch, wenn nur eine Währung zur Wahl ist** — dann sagt es, in
 * welcher Währung der Bericht steht, und dass es keine zweite gibt, solange
 * die Kurstabelle keinen Kurs kennt.
 */
export function Anzeigewaehrungswahl({
  angebot,
  gewaehlt,
  aufWahl,
}: {
  angebot: Anzeigewaehrungen | null
  gewaehlt: string | null
  aufWahl: (waehrung: string) => void
}) {
  if (!angebot || angebot.waehlbar.length === 0) {
    return null
  }

  const wert = gewaehlt && angebot.waehlbar.includes(gewaehlt) ? gewaehlt : angebot.ausgang

  return (
    <div className="feld">
      <label htmlFor="anzeigewaehrung">{t('anzeigewaehrung.titel')}</label>
      <Auswahlfeld
        id="anzeigewaehrung"
        sorte="aufzaehlung"
        wert={wert}
        eintraege={angebot.waehlbar.map((w) => ({ wert: w, text: w }))}
        aufWahl={aufWahl}
      />
    </div>
  )
}

/**
 * Welche Währung tatsächlich umgerechnet wird: die gewählte, wenn dieser
 * Bericht sie anbietet und sie nicht schon seine eigene ist — sonst keine.
 *
 * **Die Wahl bleibt stehen**, auch wo ein Bericht sie nicht anbietet: Wer vom
 * Konzern in USD kurz auf die Holding sieht, findet beim Zurückkehren wieder
 * USD.
 */
export function wirksameAnzeigewaehrung(
  gewaehlt: string | null,
  angebot: Anzeigewaehrungen | null,
): string | null {
  return gewaehlt && angebot && angebot.waehlbar.includes(gewaehlt) && gewaehlt !== angebot.ausgang
    ? gewaehlt
    : null
}

/**
 * **In welcher Währung ein Bericht geblieben ist** — nur, wenn er nicht
 * umgerechnet werden konnte, weil ein Kurs dort fehlt, wo er einen Betrag
 * trifft (2.12 Abschnitt 3.26, R1116). Sonst `null`.
 */
export function nichtUmgerechnetIn(umrechnung: Umrechnung | null | undefined): string | null {
  return umrechnung && !umrechnung.umgerechnet ? umrechnung.von : null
}

/**
 * **Über der Betragsspalte eines Nachweises steht die Währung seiner Zahlen**
 * (2.12 Abschnitt 3.22, R997) — „Betrag (USD)". Bis zum 2026-10-03 stand dort
 * fest „Betrag (Konzernwährung)", auch über den USD einer Tochter und über
 * jeder anderen Anzeigewährung. Eine Stelle für den Wertenachweis und den
 * Nachweis der Kapitalflussrechnung.
 */
export function betragskopf(waehrung: string | null | undefined): string {
  return waehrung ? t('nachweis.spalte.betragin', { waehrung }) : t('nachweis.spalte.betrag')
}

/**
 * **Die Spalte „Betrag (Landeswährung)" steht nur zur Wahl, wo sie etwas
 * zeigt** (R998): sobald eine Zeile in einer anderen Währung steht als der
 * Nachweis. Bei einer Gesellschaft in ihrer eigenen Währung wiederholte sie
 * nur die Betragsspalte daneben.
 */
export function mitLandeswaehrung(satz: {
  waehrung: string
  zeilen: { waehrung: string | null }[]
}): boolean {
  return satz.zeilen.some((z) => z.waehrung !== null && z.waehrung !== satz.waehrung)
}
